import { db, type Category } from './database'
import { pull, writeLocal } from './sync'
import { getProfile } from './auth'
import { newId } from '../lib/uuid'

/**
 * Premier lancement : créer le portefeuille et les catégories de départ.
 *
 * Appelé seulement une fois le portail d'entrée franchi (décision
 * 2026-08-24 — voir `App.tsx`, où `ensureBootstrapped()` est conditionné à
 * `signedIn`). Le réseau vient donc de répondre à l'instant (login/signup) :
 * les ids étant générés côté client, le portefeuille local remontera tel
 * quel à la première synchronisation, où le serveur l'adopte.
 */

const CURRENT_WALLET = 'current_wallet_id'
const API = import.meta.env?.VITE_API_URL ?? '/api/v1'

export async function currentWalletId(): Promise<string | null> {
  const entry = await db.meta.get(CURRENT_WALLET)
  return (entry?.value as string) ?? null
}

/**
 * Une seule exécution partagée, même si l'appel est fait plusieurs fois.
 *
 * React exécute volontairement les effets DEUX FOIS en mode strict. Les deux
 * appels lisaient « pas encore de portefeuille » avant que l'un des deux n'ait
 * écrit : résultat, deux portefeuilles et vingt-deux catégories, constatés dans
 * le navigateur le 2026-08-21. Le même enchevêtrement se produit avec deux
 * onglets ouverts en même temps.
 */
let inFlight: Promise<string> | null = null

export function ensureBootstrapped(): Promise<string> {
  // Mémorisée seulement LE TEMPS DU VOL, jamais après. Garder le résultat
  // indéfiniment ferait survivre un identifiant à la base qui le contenait —
  // effacement des données du site, base recréée — et l'app pointerait alors
  // vers un portefeuille disparu. `run()` étant idempotente, un second appel
  // séquentiel ne coûte que deux lectures.
  inFlight ??= run().finally(() => { inFlight = null })
  return inFlight
}

async function run(): Promise<string> {
  const { id, adopted } = await claimWalletId()

  /*
   * ⚠️ Un portefeuille ADOPTÉ ne se recrée pas ici — correction 2026-09-06.
   *
   * Écrire une ligne neuve pour cet identifiant la mettrait dans l'outbox avec
   * `expected_income: null`, ce qui écraserait le vrai portefeuille à la
   * première synchro ET redemanderait l'écran de bienvenue. On va le chercher
   * au serveur, et on attend : ouvrir l'app sur un portefeuille vide qui se
   * remplit sous les yeux se lit comme une perte de données.
   */
  if (adopted) {
    // Un échec réseau ne doit pas bloquer l'entrée : le bouton « Synchroniser »
    // du Profil reprendra. Rien n'est créé entre-temps, donc rien à réconcilier.
    await pull().catch(() => { /* hors ligne : on ouvrira sur un écran vide */ })
    return id
  }

  // Volontairement séparé de la réservation : si l'app est tuée entre les deux,
  // le prochain démarrage retrouve l'identifiant réservé et complète ce qui
  // manque, au lieu de pointer vers un portefeuille qui n'existe pas.
  await ensureWallet(id)
  await ensureCategories(id)
  return id
}

/**
 * Réserve l'identifiant du portefeuille de façon atomique.
 * Dexie sérialise les transactions sur une même table : le premier à poser la
 * clé gagne, les autres relisent la sienne.
 *
 * `adopted` distingue les deux façons d'obtenir cet identifiant : en créer un
 * (premier appareil du compte) ou reprendre celui que le serveur a annoncé à
 * la connexion (tous les suivants).
 */
async function claimWalletId(): Promise<{ id: string; adopted: boolean }> {
  // Le portefeuille que le compte possède déjà, le plus ancien d'abord : c'est
  // celui qu'il a ouvert. Sans cette question, chaque appareil s'en fabriquait
  // un à lui, et le compte finissait avec autant de portefeuilles que
  // d'appareils — dont un seul visible sur chacun.
  const known = getProfile()?.wallet_ids?.[0] ?? null
  const candidate = known ?? newId()

  return db.transaction('rw', db.meta, async () => {
    const existing = await db.meta.get(CURRENT_WALLET)
    // Cet appareil a déjà le sien : on n'y touche pas, même si le compte en
    // possède un autre. Le déplacer d'autorité ferait disparaître de l'écran
    // tout ce qui a été saisi ici.
    if (existing) return { id: existing.value as string, adopted: false }
    await db.meta.put({ key: CURRENT_WALLET, value: candidate })
    return { id: candidate, adopted: known !== null }
  })
}

async function ensureWallet(walletId: string): Promise<void> {
  if (await db.wallets.get(walletId)) return
  // Cycle mensuel par défaut : c'est le rythme du salaire pour la majorité.
  // L'utilisateur bascule en hebdomadaire depuis son profil si ses revenus
  // tombent à la semaine.
  // `expected_income` reste nul : c'est ce qui déclenche l'accueil du premier
  // lancement, où l'utilisateur choisit son rythme et pose son montant.
  await writeLocal('wallets', {
    id: walletId, name: 'Mon portefeuille', cycle: 'monthly',
    expected_income: null, manual_category_order: false,
  })
}

/**
 * Le catalogue tel que l'administrateur l'a défini dans `/fitiavana` — seule
 * source de vérité, plus de repli codé en dur (décision 2026-09-06 : un
 * filet client masquait silencieusement un catalogue serveur resté vide,
 * sans jamais remonter l'anomalie à l'admin).
 *
 * Une seule tentative, sans réessai : le premier lancement ne doit pas se
 * bloquer sur un réseau lent. Un échec ici ne crée aucune catégorie —
 * `ensureCategories` étant idempotent, le prochain lancement retente tout
 * seul tant qu'aucune catégorie n'a encore été écrite.
 */
async function fetchCatalogue(): Promise<
  Array<Pick<Category, 'name' | 'direction' | 'icon'>>
> {
  try {
    const response = await fetch(`${API}/categories/defaults`)
    if (!response.ok) return []
    const payload = await response.json()
    const rows = payload.categories as Array<Pick<Category, 'name' | 'direction' | 'icon'>>
    return Array.isArray(rows) ? rows : []
  } catch {
    return []
  }
}

/** Idempotent par nom : un démarrage interrompu ne laisse pas un jeu incomplet. */
async function ensureCategories(walletId: string): Promise<void> {
  const existing = await db.categories.where({ wallet_id: walletId }).toArray()
  const known = new Set(existing.map((category) => category.name))

  // ⚠️ Chaque portefeuille reçoit sa PROPRE copie. Renommer un modèle côté
  // serveur ne réécrit donc jamais l'historique de qui que ce soit — un
  // historique d'argent qui bouge tout seul est indiscernable d'un bug.
  const catalogue = known.size > 0 ? [] : await fetchCatalogue()

  for (const [index, category] of catalogue.entries()) {
    if (known.has(category.name)) continue
    await writeLocal('categories', {
      id: newId(),
      wallet_id: walletId,
      name: category.name,
      direction: category.direction,
      icon: category.icon,
      position: index,
    })
  }
}
